// What the player chose for the voice chat (microphone volume, listening volume, noise gate, how the mic is
// opened). It is kept in localStorage and sent to the game each time the voice connects: the server's own
// values are only the starting point (client: voiceChatService.ts, "voice-settings" messages and the
// "voiceReady" event).

export type MicMode = 'ptt' | 'ptm';

export interface VoicePrefs {
  inputGain: number;
  outputVolume: number;
  noiseGateEnabled: boolean;
  noiseGateThreshold: number;
  mode: MicMode;
}

const STORE_KEY = 'skymp5-voice-prefs';

// What the server announced when the voice connected: the values the sliders start from
let serverDefaults: VoicePrefs | null = null;

export const FALLBACK_PREFS: VoicePrefs = {
  inputGain: 1,
  outputVolume: 1,
  noiseGateEnabled: false,
  noiseGateThreshold: 0.01,
  mode: 'ptt',
};

export const setServerDefaults = (d: VoicePrefs): void => {
  serverDefaults = d;
};

export const getServerDefaults = (): VoicePrefs | null => serverDefaults;

export const loadSavedPrefs = (): Partial<VoicePrefs> | null => {
  try {
    const raw = window.localStorage.getItem(STORE_KEY);
    return raw ? (JSON.parse(raw) as Partial<VoicePrefs>) : null;
  } catch (e) {
    return null;
  }
};

export const savePrefs = (p: VoicePrefs): void => {
  try {
    window.localStorage.setItem(STORE_KEY, JSON.stringify(p));
  } catch (e) {
    // no storage: the choice lasts until the game closes
  }
};

export const sendPrefs = (p: VoicePrefs): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'voice-settings', action: 'apply', ...p }));
  } catch (e) {
    // Running outside the game (e.g. Storybook)
  }
};

// The prefs in force: the saved ones over the server's, over the fallback
export const currentPrefs = (): VoicePrefs => ({ ...FALLBACK_PREFS, ...(serverDefaults ?? {}), ...(loadSavedPrefs() ?? {}) });
